import { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { b2bApi } from '../api/client';
import { LayoutDashboard, GitBranch, DollarSign, Clock } from 'lucide-react';

const NAV = [
  { to: '/b2b', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/b2b/referrals', label: 'Referrals', icon: GitBranch },
  { to: '/b2b/commissions', label: 'Commissies', icon: DollarSign },
];

export default function B2BLayout() {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    if (isLoading) return;
    if (!user) { navigate('/login'); return; }
    b2bApi.status().then(({ data }) => {
      if (!data.isB2B) { setStatus('none'); navigate('/register/b2b'); }
      else if (data.isApproved) setStatus('approved');
      else setStatus('pending');
    }).catch(() => { setStatus('none'); navigate('/register/b2b'); });
  }, [user, isLoading, navigate]);

  if (isLoading || status === 'loading') return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin" />
    </div>
  );

  if (status === 'none') {
    return null; // useEffect handles redirect
  }

  if (status === 'pending') return (
    <div className="min-h-[60vh] flex items-center justify-center px-6">
      <div className="text-center max-w-sm">
        <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-5">
          <Clock className="w-6 h-6 text-amber-700" />
        </div>
        <h2 className="font-serif text-3xl font-light mb-3 text-foreground">Aanvraag in behandeling</h2>
        <p className="text-muted-foreground text-sm leading-relaxed">
          Uw B2B-aanvraag wordt beoordeeld door ons team. U ontvangt toegang zodra uw profiel is goedgekeurd.
        </p>
      </div>
    </div>
  );

  return (
    <div data-testid="b2b-layout" className="flex min-h-[calc(100vh-64px)]">
      <aside className="w-52 shrink-0 bg-sidebar border-r border-sidebar-border flex flex-col">
        <div className="px-5 py-5 border-b border-sidebar-border">
          <p className="font-serif text-sidebar-foreground text-lg font-medium">B2B Portaal</p>
          <p className="text-xs text-muted-foreground mt-0.5">{user?.name || user?.email}</p>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to} to={to} end={end}
              data-testid={`b2b-nav-${to.split('/').pop() || 'dashboard'}`}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-sidebar-accent hover:text-foreground'
                }`
              }
            >
              <Icon className="w-4 h-4 shrink-0" />{label}
            </NavLink>
          ))}
        </nav>
      </aside>
      <main className="flex-1 overflow-auto bg-background"><Outlet /></main>
    </div>
  );
}
